import React, { useState, lazy } from 'react';
import { groupStack, resolveChips, type StackChip } from './techMeta';
/** Generic glyph for concept/practice items with no brand logo. */
function GenericGlyph() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[11px] w-[11px] shrink-0" fill="none" stroke="#57534e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-170" data-mp="3jkhxnvn20yrg">
      <polyline points="8 6 3 12 8 18" data-id="element-171" data-mp="3tguxzcd3uav6" />
      <polyline points="16 6 21 12 16 18" data-id="element-172" data-mp="3oj2xvv2b3ifw" />
    </svg>;
}
/** One chip: always shows an icon (brand logo, or a generic glyph) + label. */
function Chip({
  item
}: {
  item: StackChip;
}) {
  const [iconOk, setIconOk] = useState(Boolean(item.slug));
  const showBrand = item.slug && iconOk;
  return <span className="inline-flex items-center gap-1.5 rounded-sm border border-[var(--line-strong)] bg-white/60 px-1.5 py-[2px] font-mono text-[10px] text-[var(--ink-soft)]" data-id="element-173" data-mp="25up83qc7l36">
      {showBrand ? <img src={`https://cdn.simpleicons.org/${item.slug}/57534e`} alt="" aria-hidden="true" loading="lazy" onError={() => setIconOk(false)} className="h-[11px] w-[11px] object-contain" data-id="element-174" data-mp="b2c0r82m5kzg" /> : <GenericGlyph data-id="element-175" data-mp="irso72lpfs6w" />}
      {item.label}
    </span>;
}
/**
 * Grouped, icon-bearing tech stack. Groups (Frontend / Backend & Cloud /
 * Practices) are separated by hairline rules with a mono label, so every
 * role's stack reads consistently.
 */
/**
 * Compact, single-row variant (no group labels) for condensed listings such as
 * the earlier-experience section. Still icon-bearing and consistent.
 */
export function InlineStack({
  stack
}: {
  stack: string[];
}) {
  const items = groupStack(stack).flatMap(g => g.items);
  if (items.length === 0) return null;
  return <div className="mt-1.5 flex flex-wrap gap-1" data-id="element-176" data-mp="1alumn8h6qgl5">
      {items.map(item => <Chip key={item.label} item={item} data-id="element-177" data-mp="1fiuxdwozc7il" />)}
    </div>;
}
/** Icon chips for an ordered list of labels (used by the Capabilities index). */
export function ChipRow({
  labels
}: {
  labels: string[];
}) {
  const items = resolveChips(labels);
  if (items.length === 0) return null;
  return <div className="flex flex-wrap gap-1.5" data-id="element-178" data-mp="3lcu6e3ee4z7q">
      {items.map(item => <Chip key={item.label} item={item} data-id="element-179" data-mp="3gm444z19hch9" />)}
    </div>;
}
export function TechStack({
  stack
}: {
  stack: string[];
}) {
  const groups = groupStack(stack);
  if (groups.length === 0) return null;
  return <div className="avoid-break mt-4 border-t border-[var(--line)] pt-3" data-id="element-180" data-mp="3qc6uerh00cbw">
      <p className="mb-2.5 font-mono text-[9px] uppercase tracking-[0.24em] text-[var(--ink-faint)]" data-id="element-181" data-mp="86280kjfahlb">
        Stack
      </p>
      <div className="space-y-2.5" data-id="element-182" data-mp="36fsu5k1ar9v">
        {groups.map(g => <div key={g.group} className="flex flex-wrap items-start gap-x-3 gap-y-1.5" data-id="element-183" data-mp="354p25ql9jir">
            <span className="mt-[3px] w-[92px] shrink-0 font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--accent)]" data-id="element-184" data-mp="24yu9z2er7rp8">
              {g.group}
            </span>
            <div className="flex flex-1 flex-wrap gap-1.5" data-id="element-185" data-mp="kimhlw28jytb">
              {g.items.map(item => <Chip key={item.label} item={item} data-id="element-186" data-mp="ph7h1t9nyegp" />)}
            </div>
          </div>)}
      </div>
    </div>;
}